import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import { useLazyQuery } from '@apollo/client';
import { Button, Carousel, Container, Row, Column, Icon, classNames } from '@pella/sash-framework';
import { CTA, Image, LoadingOverlay } from '../../';
import { contentstackClient } from '../../../utils/contentstack-client';
import { GET_INSPIRATION_BY_TAXONOMY } from '../../../enums/contentstack-graphql';
import { setLocalStorage, getLocalStorage, removeLocalStorage } from '../../utilities/functions';
import { isValidArray } from '../../utilities/validations';

import FilterButton from './filter-button';

import styles from './index.module.scss';

const DynamicInspiration = ({ data, dynamic, className, toggles }) => {
	const { filter_groups } = useStaticQuery(query).contentstackCategoryInspirationConfig;
	const filters = filter_groups?.find((group) => group.filter_tag[0] === dynamic?.product_type)?.group;
	const [inspiration, setInspiration] = useState({ entries: [], total: 0, isInitialLoad: true });
	const [selectedFilters, setSelectedFilters] = useState({ base: dynamic?.filter_tags });
	const [showFilters, setShowFilters] = useState(false);
	const [loadMore, setLoadMore] = useState(false);
	const [getInspiration, { loading }] = useLazyQuery(GET_INSPIRATION_BY_TAXONOMY, {
		client: contentstackClient,
		skip: !isValidArray(dynamic?.filter_tags),
		onCompleted: (data) => onLoadCompleted(data.all_image_detail),
	});

	const carouselClasses = classNames({
		'md:grid md:gap-5 md:grid-flow-row md:auto-rows-[320px] xl:auto-rows-[415px] md:overflow-visible [&>li]:w-full': true,
		'md:grid-cols-4 [&>li:first-child]:col-span-2 [&>li:nth-child(6)]:col-span-2': ![2, 4, 5].includes(inspiration?.entries?.length),
		'md:grid-cols-2': [2, 4].includes(inspiration?.entries?.length),
		'[&>li:nth-child(4)]:col-span-2 [&>li:last-child]:col-span-2': inspiration?.entries?.length === 5,
		'[&>li:nth-child(6n+1)]:col-span-2 [&>li:nth-child(6n)]:col-span-2': inspiration?.entries?.length > 6,
		'[&>li:nth-child(n+7)]:hidden': inspiration?.entries?.length > 6 && !loadMore,
	});

	useEffect(() => {
		if (isValidArray(dynamic?.filter_tags)) {
			getInspiration({ variables: { filters: restructureFilters(selectedFilters), limit: 12, skip: 0 } });
		}

		let scrollPoint = getLocalStorage('scrollPoint');
		if (scrollPoint && scrollPoint.closeURL && scrollPoint.position) {
			if (typeof window !== 'undefined' && typeof scrollTo !== 'undefined') {
				(window.location.pathname == scrollPoint.closeURL &&
					setTimeout(() => {
						scrollTo(scrollPoint.position.offSetX, scrollPoint.position.offSetY);
					}, 200)) ||
					'';
				removeLocalStorage('scrollPoint');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	if (!isValidArray(dynamic?.filter_tags)) return null;

	function onLoadCompleted(array) {
		if (!isValidArray(array.items)) {
			setInspiration({ entries: [], total: 0, isInitialLoad: true });

			return;
		}

		let newArr;

		if (!loadMore) {
			newArr = array.items;
		} else {
			newArr = [...inspiration.entries, ...array.items];
			setLoadMore(false);
		}

		setInspiration({ entries: newArr, total: array.total, isInitialLoad: true });
	}

	const onFilterOptionClick = (name, option) => {
		let newFilters = { ...selectedFilters };
		newFilters[name] = option;

		setInspiration({ entries: [], total: 0, isInitialLoad: true });

		getInspiration({ variables: { filters: restructureFilters(newFilters), limit: 12, skip: 0, isInitialLoad: false } });

		setSelectedFilters(newFilters);
	};

	const onInspirationCardClick = (url) => {
		if (typeof window !== 'undefined') {
			let position = {
				offsetX: 0,
				offSetY: window.scrollY || 0,
			};

			const flattenURLs = inspiration.entries.map((item) => item.url);
			if (isValidArray(flattenURLs)) {
				const imgDetailData = {
					position,
					slideData: flattenURLs,
					currentUrl: window.location.pathname,
				};
				setLocalStorage('imgDetailData', imgDetailData);
			} else {
				const imgDetailData = {
					position,
					currentUrl: window.location.pathname,
				};
				setLocalStorage('imgDetailData', imgDetailData);
			}

			navigate(url);
		}
	};

	const onLoadMoreClick = () => {
		setLoadMore(true);
		setInspiration({ ...inspiration, isInitialLoad: false });
	};

	const onFilterGalleryClick = () => setShowFilters(!showFilters);

	const restructureFilters = (filters) =>
		Object.values(filters)
			.filter((value) => Boolean(value))
			.flat()
			.map((value) => ({
				taxonomies: {
					filter_by: {
						term_in: [...value?.split(', ')],
					},
				},
			}));

	return (
		<div className={`${styles.dynInsp} ${className || ''} ${toggles?.inspiration === 0 ? 'hidden' : ''}`}>
			<Container>
				{data.heading && <h2 className='mb-5 md:mb-7 xl:mb-10'>{data.heading}</h2>}

				<button
					onClick={onFilterGalleryClick}
					className={`${styles.filterMobileBtn} mt-4 flex w-full flex-row items-center text-left md:hidden`}
				>
					<span className='flex-1 text-xs font-bold tracking-wider'>Filter Gallery</span>
					<Icon
						name={showFilters ? 'remove' : 'add'}
						className='flex-initial'
						size={20}
					/>
				</button>

				<div className={`${styles.dynInsp_Filters} ${showFilters ? 'mt-4 block md:mt-0' : 'hidden md:block'} md:mt-4`}>
					<Row>
						{isValidArray(filters) &&
							filters.map((filter, index) => (
								<Column
									key={index}
									xs={12}
									md={3}
									className='mb-2 md:mb-0'
								>
									<FilterButton
										data={filter}
										selected={selectedFilters[filter.category]}
										clearFilter={() => onFilterOptionClick(filter.category, null)}
										disabled={filter.filters.length === 0}
									>
										{filter.filters.map((option, index) => (
											<button
												key={index}
												onClick={() => onFilterOptionClick(filter.category, option.filter_tag)}
												className={styles.dynInsp_FilterOptionBtn}
											>
												{option.display_name}
											</button>
										))}
									</FilterButton>
								</Column>
							))}
					</Row>
				</div>
			</Container>

			<div className={styles.dynInsp_ImageGridWrapper}>
				<Container
					className='relative mt-5'
					noPadding
				>
					{loading && <LoadingOverlay fixed={false} />}

					{isValidArray(inspiration.entries) && (
						<Carousel
							wrapperClassName='md:[&>div]:overflow-visible'
							className={carouselClasses}
							slidesToShow={{ xs: 1, sm: 2 }}
							arrowsClassName='md:hidden'
							slidePadding={16}
							showArrows
							showSlideHints
						>
							{inspiration.entries.map((item, index) => (
								<a
									key={index}
									href={item.url}
									onClick={(event) => {
										event.preventDefault();
										onInspirationCardClick(item.url);
									}}
									aria-label={data.imageConnection?.edges?.[0]?.node?.imageConnection?.edges?.[0]?.node?.image_alt}
								>
									{toggles?.inspiration !== 0 && (
										<Image
											data={{ image: item?.imageConnection?.edges?.[0]?.node?.imageConnection?.edges?.[0]?.node }}
											params='?format=pjpg&auto=webp&quality=90'
											className='!block h-full w-full !object-cover transition-shadow duration-300 hover:shadow-lg'
										/>
									)}
								</a>
							))}
						</Carousel>
					)}

					{!isValidArray(inspiration.entries) && (
						<div className={styles.dynInsp_EmptyState}>
							<svg
								width='276'
								height='276'
								viewBox='0 0 276 276'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
							>
								<rect
									width='276'
									height='276'
									fill='white'
								/>
								<rect
									x='40'
									y='40'
									width='197'
									height='197'
									rx='98'
									fill='#FFDE00'
								/>
								<rect
									x='78'
									y='40'
									width='121'
									height='177'
									fill='white'
									stroke='black'
									strokeWidth='1.4'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<rect
									x='85'
									y='47'
									width='107'
									height='164'
									stroke='black'
									strokeWidth='1.4'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<rect
									x='126'
									y='205'
									width='25'
									height='6'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<rect
									x='90'
									y='53'
									width='97'
									height='152'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M90 53H137V128H90V53Z'
									fill='#FFDE00'
									stroke='black'
									strokeWidth='0.8'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M90 130H137V205H90V130Z'
									fill='#FFDE00'
									stroke='black'
									strokeWidth='0.8'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M140 53H187V128H140V53Z'
									fill='#FFDE00'
									stroke='black'
									strokeWidth='0.8'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M140 130H187V205H140V130Z'
									fill='#FFDE00'
									stroke='black'
									strokeWidth='0.8'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M90 53L85 47'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M187 53L192 47'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M187 205L192 211'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M90 205L85 211'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M211 217H66C65.4477 217 65 217.448 65 218V221C65 221.552 65.4477 222 66 222H66.8C68.5673 222 70 223.433 70 225.2C70 225.642 70.3582 226 70.8 226H206.7C207.142 226 207.5 225.642 207.5 225.2C207.5 223.433 208.933 222 210.7 222H211.1C211.597 222 212 221.597 212 221.1V218C212 217.448 211.552 217 211 217Z'
									fill='white'
									stroke='black'
									strokeWidth='1.4'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M68 222H210'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M126 208H151'
									stroke='black'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
							</svg>
							<p className={styles.dynInsp_EmptyStateHeading}>There are no results matching your criteria.</p>
							<p className={styles.dynInsp_EmptyStateDescription}>Please try expanding your search.</p>
						</div>
					)}
				</Container>
			</div>

			<Container>
				{isValidArray(inspiration.entries) && inspiration.total > 12 && inspiration.isInitialLoad && (
					<div className='mt-4 hidden text-center md:block xl:mt-6'>
						<Button
							color='tertiary-alt'
							onClick={onLoadMoreClick}
						>
							Load More
						</Button>
					</div>
				)}

				{isValidArray(dynamic?.ctas) && (
					<div className='mt-4 text-center xl:mt-6'>
						<CTA
							data={dynamic.ctas[0]}
							className='decoration-black'
						/>
					</div>
				)}
			</Container>
		</div>
	);
};

DynamicInspiration.propTypes = {
	data: PropTypes.object,
	dynamic: PropTypes.object,
	tags: PropTypes.array,
	productType: PropTypes.string,
	className: PropTypes.string,
	toggles: PropTypes.object,
};

export default DynamicInspiration;

export const query = graphql`
	{
		contentstackCategoryInspirationConfig {
			filter_groups {
				filter_tag
				group {
					category
					filters {
						filter_tag
						display_name
					}
				}
			}
		}
	}
`;
